import React, { useState, useEffect, useRef } from 'react'
import { Auth, Hub } from 'aws-amplify'
import { useHistory } from 'react-router-dom'
import ReactGA from 'react-ga'
import * as Sentry from '@sentry/browser'

import { CognitoUser } from 'amazon-cognito-identity-js'
import { HubCapsule, HubPayload } from '@aws-amplify/core/lib-esm/Hub'

// A simple synchronous cache of the user's logged-in state.
// This will be verified with Cognito, so it's no big problem.
// Just a mechanism to keep routes working.
const updateLoggedInStatusCache = (status: boolean) => {
  localStorage.loggedIn = JSON.stringify(status)
}
const loggedInStatusCache = () => {
  return localStorage.loggedIn && JSON.parse(localStorage.loggedIn)
}

const initialSessionState = () => ({
  loggedIn: loggedInStatusCache(),
})

interface SessionContextData {
  loggedIn: boolean
  user?: CognitoUser
  logout?: () => void
}

export const SessionContext = React.createContext<SessionContextData>(initialSessionState())

export const SessionContextProvider: React.FC = ({children}) => {
  const history = useHistory()
  const historyRef = useRef(history)
  const [ state, setState ] = useState<SessionContextData>(initialSessionState())

  useEffect(() => {
    const onHubCapsule = (capsule: HubCapsule) => {
      const { channel, payload } = capsule
      if (channel === 'auth') { onAuthEvent(payload) }
    }

    const onAuthEvent = async (payload: HubPayload) => {
      const { event } = payload
      if (event === 'signIn') {
        updateLoggedInStatusCache(true)
        const user = await Auth.currentAuthenticatedUser()
        ReactGA.set({userId: user.username})
        Sentry.configureScope((scope) => {
          scope.setUser({
            id: user.username,
            email: user.attributes.email,
          })
        })
        setState({
          loggedIn: true,
          user
        })
      } else if (event === 'signOut') {
        updateLoggedInStatusCache(false)
        setState(initialSessionState())
      }
    }

    Hub.listen('auth', onHubCapsule, 'AuthSessionListener')

    setState(s => ({
      ...s,
      logout: async () => {
        await Auth.signOut()
        historyRef.current.push("/")
      }
    }))

    Auth
      .currentAuthenticatedUser()
      .then((user) => {
        updateLoggedInStatusCache(true)
        ReactGA.set({userId: user.username})
        Sentry.configureScope((scope) => {
          scope.setUser({
            id: user.username,
            email: user.attributes.email,
          })
        })
        setState(s => ({
          ...s,
          loggedIn: true,
          user
        }))
      })
      .catch(() => {
        updateLoggedInStatusCache(false)
        setState(initialSessionState())
      })

  }, [ historyRef, setState ])

  return (
    <SessionContext.Provider value={state}>
      {children}
    </SessionContext.Provider>
  )
}
